import { ChartsItemContentProps } from "@mui/x-charts";
import { getLabel, CommonSeriesType } from "@mui/x-charts/internals";
import { useTranslation } from "react-i18next";

import { ChartTooltip } from "glue/Chart/components/ChartTooltip";
import { useAssetFilterTranslations } from "hooks";
import { EPCClassification } from "legacyNext/components";

import { AssetRiskSeriesDefaultized } from "../asset-risk.types";

type Props = {
    series: AssetRiskSeriesDefaultized;
    years?: Date[];
} & ChartsItemContentProps<"line">;

export const AssetRiskChartTooltip = (props: Props) => {
    const { series, itemData, getColor, years } = props;
    const { dataIndex } = itemData;

    const { t } = useTranslation();

    const { translations } = useAssetFilterTranslations();
    if (!dataIndex || !series.data[dataIndex] || !years) {
        return null;
    }

    const color = getColor(dataIndex);

    const value = series.data[dataIndex];

    const formattedValue = (
        series.valueFormatter as CommonSeriesType<
            typeof value
        >["valueFormatter"]
    )?.(value, { dataIndex });

    const assetLabel = getLabel(series.label, "tooltip");
    const year = years[dataIndex];

    return (
        <ChartTooltip
            sections={[
                {
                    header: year.getFullYear(),
                    subHeaders: [
                        {
                            label: t("common.labels.intensity", "Intensity"),
                            value: formattedValue ?? "Error",
                        },
                    ],
                },
                {
                    items: [
                        {
                            label: t(
                                "assetDetailsComponent.assetType",
                                "Asset Type"
                            ),
                            value: assetLabel
                                ? translations[
                                      assetLabel as keyof typeof translations
                                  ]
                                : "Error",
                            iconColor: color,
                        },
                        {
                            label: t("assetDetailsComponent.area", "Area"),
                            value: t("legacyNext.sqm", "sqm", {
                                value: series?.totalArea?.toLocaleString(),
                            }),
                        },
                        {
                            label: t(
                                "legacyNext.energyLabels",
                                "Energy Labels"
                            ),
                            value: series.assetExternalId && (
                                <EPCClassification
                                    assetExternalId={series.assetExternalId}
                                />
                            ),
                        },
                        {
                            label: t(
                                "assetDetailsComponent.stranded",
                                "Stranded"
                            ),
                            value: series.stranded?.[dataIndex]
                                ? t("common.yes", "Yes")
                                : t("common.no", "No"),
                        },
                        {
                            label: t(
                                "assetDetailsComponent.constructionYear",
                                "Construction Year"
                            ),
                            value: series.constructionYear?.toString(),
                        },
                        {
                            label: t("common.labels.address", "Address"),
                            value: series.fullAddress?.split(",")[0],
                        },
                    ],
                },
            ]}
        />
    );
};
